import { Contract, LatestTariff } from '@/src/api/contracts/types';
import { isChargeAnytimeProduct } from '@/src/pages/AnytimeEligibilityV2/hooks/useChargeAnytimeBoltonState';
import { ProductsResponse } from '@/src/types/Response';
import {
  isGreenerElectricityBundleCustomer,
  isGreenerElectricityCustomer,
} from '@/src/utils/products';

const activeContracts = (contracts: Contract[]): Contract[] =>
  contracts.filter(c => c.details.status.active);

const ELIGIBLE_PAYMENT_METHODS: LatestTariff['paymentMethod'][] = [
  'DIRECT_DEBIT',
  'ON_DEMAND',
];

// Payment method is per-contract, not per-account: any active prepayment supply rules the account out.
export const getIsEligiblePaymentMethod = (
  contracts: Contract[] | undefined,
): boolean | undefined => {
  if (!contracts) return undefined;
  const active = activeContracts(contracts);
  if (active.length === 0) return false;
  return active.every(c =>
    ELIGIBLE_PAYMENT_METHODS.includes(c.details.latestTariff.paymentMethod),
  );
};

// Pre-supply (signed up, supply hasn't started) is out of scope for beta, as is
// an account whose supply has ended — neither has an active contract.
export const getHasStartedSupply = (
  contracts: Contract[] | undefined,
): boolean | undefined => {
  if (!contracts) return undefined;
  return activeContracts(contracts).length > 0;
};

// New web has no greener-electricity screens yet, standalone or tariff-bundled.
export const getHasNoGreenerElectricity = (
  products: ProductsResponse | undefined,
): boolean | undefined => {
  if (!products) return undefined;
  return !(
    isGreenerElectricityCustomer(products.boltons) ||
    isGreenerElectricityBundleCustomer(products.boltons)
  );
};

export const getHasNoChargeAnytime = (
  products: ProductsResponse | undefined,
): boolean | undefined => {
  if (!products) return undefined;
  return !products.boltons.some(bolton =>
    isChargeAnytimeProduct(bolton.productId),
  );
};

type HomeServicesClassification = {
  hasBoilerInsurance: boolean;
  hasAbs: boolean;
  isReady: boolean;
};

// `PRODUCT_IDS.boilerCover` is an 'XXXX' placeholder — classification is the only source of truth.
export const getHasNoHomeServices = ({
  hasBoilerInsurance,
  hasAbs,
  isReady,
}: HomeServicesClassification): boolean | undefined => {
  if (!isReady) return undefined;
  return !hasBoilerInsurance && !hasAbs;
};
